import { type ReactNode, useCallback, useRef, useEffect } from 'react';
import { generateKey } from 'utils/helpers';
import ModalsContext from './ModalsContext';

type ModalData = { id: string; onEscCallback: () => void };
type Props = { children: ReactNode };

const ModalsContextProvider = ({ children }: Props) => {
  const queueRef = useRef<ModalData[]>([]);

  useEffect(() => {
    const escHandler = (e: KeyboardEvent) => {
      const queueLength = queueRef.current.length;
      if (e.key === 'Escape' && queueLength !== 0) {
        const modalData = queueRef.current[queueLength - 1];
        modalData!.onEscCallback();
      }
    };

    document.addEventListener('keydown', escHandler);
    return () => document.removeEventListener('keydown', escHandler);
  }, []);

  const register = useCallback((onEscCallback: () => void) => {
    const modalId = generateKey();
    queueRef.current.push({ id: modalId, onEscCallback });

    const unregister = () => {
      const modalIndex = queueRef.current.findIndex(el => el.id === modalId);
      queueRef.current.splice(modalIndex, 1);
    };

    return unregister;
  }, []);

  return <ModalsContext.Provider value={{ register }}> {children} </ModalsContext.Provider>;
};

export default ModalsContextProvider;
